import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, BadgeCheck, Briefcase, Building2, HardHat, Home, Landmark, RotateCcw, Sparkles, User, Wallet } from "lucide-react";
import { SectionHeading } from "./Reveal";
import { ALL_SERVICES } from "../../lib/data";
import { goEnquire } from "../../lib/scroll";

const QUESTIONS = [
  {
    key: "purpose",
    title: "What do you need financing for?",
    options: [
      { id: "home", label: "Buy or build a home", icon: Home },
      { id: "business", label: "Grow or run my business", icon: Briefcase },
      { id: "unlock", label: "Unlock value from my property", icon: Building2 },
      { id: "project", label: "Fund a real estate project", icon: HardHat },
      { id: "strategic", label: "Strategic advisory — M&A, IPO, capital", icon: Landmark },
    ],
  },
  {
    key: "profile",
    title: "Which best describes you?",
    options: [
      { id: "salaried", label: "Salaried professional", icon: User },
      { id: "owner", label: "Self-employed / business owner", icon: Briefcase },
      { id: "corporate", label: "Company / corporate", icon: Landmark },
      { id: "developer", label: "Real estate developer", icon: HardHat },
    ],
  },
  {
    key: "amount",
    title: "How much funding do you need?",
    options: [
      { id: "lt50", label: "Under ₹50 Lakh", icon: Wallet },
      { id: "50to2", label: "₹50 Lakh – ₹2 Crore", icon: Wallet },
      { id: "2to10", label: "₹2 – ₹10 Crore", icon: Wallet },
      { id: "gt10", label: "₹10 Crore +", icon: Wallet },
    ],
  },
];

const SCORES = {
  purpose: {
    home: { "home-loan": 3, "loan-against-property": 1 },
    business: { "business-loan": 3, "working-capital-finance": 2, "corporate-finance": 1 },
    unlock: { "loan-against-property": 3, "lease-rental-discounting": 2, "commercial-property-finance": 1 },
    project: { "construction-finance": 3, "commercial-property-finance": 2, "corporate-finance": 1 },
    strategic: { "ma-advisory-execution": 3, "debt-equity-capital-structuring": 2, "sme-ipo-advisory": 2, "wealth-growth-portfolio-diversification": 1 },
  },
  profile: {
    salaried: { "home-loan": 1, "loan-against-property": 1 },
    owner: { "business-loan": 1, "working-capital-finance": 1, "sme-ipo-advisory": 1 },
    corporate: { "corporate-finance": 2, "debt-equity-capital-structuring": 1, "risk-mitigation-financial-resilience": 1 },
    developer: { "construction-finance": 2, "lease-rental-discounting": 1, "commercial-property-finance": 1 },
  },
  amount: {
    lt50: { "business-loan": 1, "home-loan": 1 },
    "50to2": { "loan-against-property": 1, "home-loan": 1 },
    "2to10": { "commercial-property-finance": 1, "corporate-finance": 1, "construction-finance": 1 },
    gt10: { "corporate-finance": 2, "debt-equity-capital-structuring": 1, "construction-finance": 1, "ma-advisory-execution": 1 },
  },
};

export const EligibilityChecker = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});

  const done = step >= QUESTIONS.length;

  const matches = useMemo(() => {
    if (!done) return [];
    const totals = {};
    Object.entries(answers).forEach(([qKey, ansId]) => {
      const table = SCORES[qKey]?.[ansId] || {};
      Object.entries(table).forEach(([slug, pts]) => {
        totals[slug] = (totals[slug] || 0) + pts;
      });
    });
    return Object.entries(totals)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([slug]) => ALL_SERVICES.find((s) => s.slug === slug))
      .filter(Boolean);
  }, [answers, done]);

  const pick = (qKey, id) => {
    setAnswers((a) => ({ ...a, [qKey]: id }));
    setStep((s) => s + 1);
  };

  const restart = () => {
    setAnswers({});
    setStep(0);
  };

  const q = QUESTIONS[step];

  return (
    <section id="eligibility" className="relative overflow-hidden bg-white py-24 md:py-32" data-testid="eligibility-section">
      <div className="pointer-events-none absolute -right-32 top-10 h-[380px] w-[380px] rounded-full bg-gold/8 blur-[150px]" />
      <div className="relative mx-auto max-w-7xl px-5 md:px-8">
        <SectionHeading
          overline="Eligibility Checker"
          title="Find Your Fit in 3 Quick Questions."
          sub="Answer three simple questions and we'll point you to the solutions that suit your requirement best."
          testId="eligibility-heading"
        />

        <div className="mx-auto mt-14 max-w-4xl rounded-2xl border border-gray-200 bg-fog p-6 shadow-[0_2px_10px_rgba(16,20,24,0.04)] md:p-10">
          <AnimatePresence mode="wait">
            {!done ? (
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 32 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -32 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`eligibility-question-${step + 1}`}
              >
                <div className="flex items-center justify-between">
                  <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-gold-deep">
                    Question {step + 1} of {QUESTIONS.length}
                  </p>
                  <div className="flex gap-1.5">
                    {QUESTIONS.map((_, i) => (
                      <span key={i} className={`h-1.5 rounded-full transition-all duration-300 ${i <= step ? "w-6 bg-gold" : "w-3 bg-gray-300"}`} />
                    ))}
                  </div>
                </div>
                <h3 className="mt-4 font-heading text-2xl font-bold tracking-tight text-navy md:text-3xl">{q.title}</h3>
                <div className="mt-8 grid gap-3 sm:grid-cols-2">
                  {q.options.map((o) => {
                    const Icon = o.icon;
                    return (
                      <button
                        key={o.id}
                        onClick={() => pick(q.key, o.id)}
                        data-testid={`eligibility-option-${o.id}`}
                        className="group flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-5 text-left transition-all duration-300 hover:-translate-y-0.5 hover:border-gold hover:shadow-[0_10px_28px_rgba(16,20,24,0.08)]"
                      >
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-gold-line bg-gold-tint text-gold-deep">
                          <Icon size={18} strokeWidth={1.7} />
                        </span>
                        <span className="text-sm font-semibold text-navy">{o.label}</span>
                        <ArrowRight size={15} className="ml-auto shrink-0 text-gray-300 transition-all duration-300 group-hover:translate-x-1 group-hover:text-gold-deep" />
                      </button>
                    );
                  })}
                </div>
                {step > 0 && (
                  <button
                    onClick={() => setStep((s) => s - 1)}
                    data-testid="eligibility-back-btn"
                    className="mt-6 flex items-center gap-2 text-sm font-semibold text-gray-500 transition-colors hover:text-navy"
                  >
                    <ArrowLeft size={14} /> Back
                  </button>
                )}
              </motion.div>
            ) : (
              <motion.div
                key="results"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                data-testid="eligibility-result"
              >
                <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-gold-deep">
                  <Sparkles size={14} /> Your Recommended Solutions
                </p>
                <h3 className="mt-4 font-heading text-2xl font-bold tracking-tight text-navy md:text-3xl">
                  These fit your requirement best
                </h3>
                <div className="mt-8 grid gap-3">
                  {matches.map((s, i) => {
                    const Icon = s.icon;
                    return (
                      <div
                        key={s.slug}
                        data-testid={`eligibility-result-card-${s.slug}`}
                        className="flex flex-col gap-4 rounded-xl border border-gray-200 bg-white p-5 transition-colors duration-300 hover:border-gold md:flex-row md:items-center"
                      >
                        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-gold-line bg-gold-tint text-gold-deep">
                          <Icon size={19} strokeWidth={1.6} />
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <h4 className="font-heading text-lg font-semibold text-navy">{s.title}</h4>
                            {i === 0 && (
                              <span className="flex items-center gap-1 rounded-full bg-gold px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-navy-deep">
                                <BadgeCheck size={11} /> Best Match
                              </span>
                            )}
                          </div>
                          <p className="mt-1 text-sm leading-relaxed text-gray-600">{s.short}</p>
                        </div>
                        <div className="flex shrink-0 flex-wrap gap-2.5">
                          <button
                            onClick={() => navigate(`/solutions/${s.slug}`)}
                            data-testid={`eligibility-know-more-${s.slug}`}
                            className="rounded-full border border-gray-300 px-4 py-2 text-xs font-semibold text-gray-800 transition-colors duration-300 hover:border-orange hover:text-orange-deep"
                          >
                            Know More
                          </button>
                          <button
                            onClick={() => goEnquire(navigate, s.title)}
                            data-testid={`eligibility-enquire-${s.slug}`}
                            className="rounded-full bg-gold px-4 py-2 text-xs font-bold text-navy-deep transition-transform duration-300 hover:scale-105"
                          >
                            Enquire Now
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-7 flex flex-wrap items-center justify-between gap-4">
                  <button
                    onClick={restart}
                    data-testid="eligibility-restart-btn"
                    className="flex items-center gap-2 text-sm font-semibold text-gray-500 transition-colors hover:text-navy"
                  >
                    <RotateCcw size={14} /> Start Over
                  </button>
                  <p className="text-xs text-gray-400">Indicative guidance only — final suitability is assessed by our advisory team.</p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};
